import classNames from 'classnames';
import { ChangeEvent, ForwardedRef, forwardRef, useEffect, useState } from 'react';
import Icon from '../Icons/Icon';
import CharacterCount from './CharacterCount/CharacterCount';
import styles from './Input.module.css';
import { InputProps, InputStates } from './Input.types';

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      hideLabel,
      onChange,
      helpText,
      subText,
      characterLimit,
      state = InputStates.Default,
      value,
      id,
      className,
      type,
      subTextClassName,
      ...props
    },
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    // Maintain internal state for value; stays in sync with prop when provided
    const [inputValue, setInputValue] = useState(value || '');

    useEffect(() => {
      setInputValue(value || '');
    }, [value]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      // Always reflect the latest typed characters immediately; parent updates will sync via useEffect
      setInputValue(newValue);
      // Forward the original event so consumers can access target.id/name, etc.
      if (onChange) {
        onChange(event);
      }
    };

    return (
      <div className={styles.container}>
        {!hideLabel && (
          <div className={styles.labelwrapper}>
            <label htmlFor={id} className={styles.label}>
              {label}
            </label>
            {helpText !== undefined && <div className={styles.helptext}>{helpText}</div>}
          </div>
        )}
        <div className={styles.inputContainer}>
          <input
            {...props}
            type={type || 'text'}
            id={id}
            ref={ref}
            onChange={handleChange}
            value={inputValue}
            className={classNames({
              [styles.input]: true,
              [styles.errorinput]: state === InputStates.Error,
              ...(className ? { [className]: true } : {}),
            })}
          />
          {type === 'time' && <Icon iconName="Clock" className={styles.dateTimeIcon} size={16} />}
          {type === 'date' && <Icon iconName="Calendar" className={styles.dateTimeIcon} size={16} />}
        </div>
        {characterLimit !== undefined && (
          <CharacterCount charactersRemaining={characterLimit - (inputValue?.length || 0)} />
        )}
        {subText !== undefined && (
          <div
            aria-live={state === InputStates.Error ? 'assertive' : 'off'}
            className={classNames({
              [styles.subtext]: true,
              [styles.errortext]: state === InputStates.Error,
              [styles.successtext]: state === InputStates.Success,
              ...(subTextClassName ? { [subTextClassName]: true } : {}),
            })}
          >
            {subText}
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
